import { TABLE_SHAPE_PRESETS, createDefaultTablePlacement, suggestTableSizeForHall } from './defaults';
export const ARRANGEMENT_OPTIONS = [
    {
        id: 'grid',
        label: 'Grid',
        tip: 'Even rows & columns',
        icon: 'grid-outline',
        minTables: 2,
    },
    {
        id: 'rows',
        label: 'Rows',
        tip: 'Two dining aisles',
        icon: 'menu-outline',
        minTables: 2,
    },
    {
        id: 'circle',
        label: 'Circle',
        tip: 'Around the center',
        icon: 'ellipse-outline',
        minTables: 3,
    },
    {
        id: 'wall',
        label: 'Walls',
        tip: 'Along the edges',
        icon: 'square-outline',
        minTables: 2,
    },
];
export const LAYOUT_TEMPLATES = [
    {
        id: 'cafe-6',
        label: 'Cafe · 6',
        tip: '2×3 square tables',
        slots: [
            { shape: 'square', capacity: 2, x: 0.12, y: 0.18 },
            { shape: 'square', capacity: 2, x: 0.42, y: 0.18 },
            { shape: 'square', capacity: 2, x: 0.72, y: 0.18 },
            { shape: 'square', capacity: 4, x: 0.12, y: 0.55 },
            { shape: 'square', capacity: 4, x: 0.42, y: 0.55 },
            { shape: 'square', capacity: 4, x: 0.72, y: 0.55 },
        ],
    },
    {
        id: 'dining-8',
        label: 'Dining · 8',
        tip: 'Mix of round & rect',
        slots: [
            { shape: 'round', capacity: 4, x: 0.1, y: 0.15 },
            { shape: 'round', capacity: 4, x: 0.4, y: 0.15 },
            { shape: 'round', capacity: 4, x: 0.7, y: 0.15 },
            { shape: 'rectangle', capacity: 6, x: 0.08, y: 0.52 },
            { shape: 'rectangle', capacity: 6, x: 0.4, y: 0.52 },
            { shape: 'square', capacity: 4, x: 0.72, y: 0.5 },
            { shape: 'booth', capacity: 4, x: 0.1, y: 0.78 },
            { shape: 'booth', capacity: 4, x: 0.55, y: 0.78 },
        ],
    },
    {
        id: 'bistro-4',
        label: 'Bistro · 4',
        tip: 'Cozy center cluster',
        slots: [
            { shape: 'round', capacity: 2, x: 0.22, y: 0.28 },
            { shape: 'round', capacity: 2, x: 0.55, y: 0.28 },
            { shape: 'round', capacity: 4, x: 0.22, y: 0.58 },
            { shape: 'round', capacity: 4, x: 0.55, y: 0.58 },
        ],
    },
    {
        id: 'bar-row',
        label: 'Bar · 5',
        tip: 'Counter + nearby 2-tops',
        slots: [
            { shape: 'bar', capacity: 4, x: 0.08, y: 0.12 },
            { shape: 'bar', capacity: 4, x: 0.5, y: 0.12 },
            { shape: 'square', capacity: 2, x: 0.15, y: 0.45 },
            { shape: 'square', capacity: 2, x: 0.42, y: 0.45 },
            { shape: 'square', capacity: 2, x: 0.69, y: 0.45 },
        ],
    },
    {
        id: 'banquet-10',
        label: 'Banquet · 10',
        tip: 'Grid for larger parties',
        slots: [
            { shape: 'rectangle', capacity: 6, x: 0.08, y: 0.12 },
            { shape: 'rectangle', capacity: 6, x: 0.4, y: 0.12 },
            { shape: 'rectangle', capacity: 6, x: 0.72, y: 0.12 },
            { shape: 'rectangle', capacity: 8, x: 0.08, y: 0.42 },
            { shape: 'rectangle', capacity: 8, x: 0.4, y: 0.42 },
            { shape: 'rectangle', capacity: 8, x: 0.72, y: 0.42 },
            { shape: 'round', capacity: 6, x: 0.08, y: 0.72 },
            { shape: 'round', capacity: 6, x: 0.34, y: 0.72 },
            { shape: 'round', capacity: 6, x: 0.6, y: 0.72 },
            { shape: 'booth', capacity: 6, x: 0.78, y: 0.72 },
        ],
    },
];
export const resolvePresetForSlot = (slot) => {
    const match = TABLE_SHAPE_PRESETS.find(preset => preset.shape === slot.shape && preset.capacity === slot.capacity);
    if (match) {
        return match;
    }
    return (TABLE_SHAPE_PRESETS.find(preset => preset.shape === slot.shape) ||
        TABLE_SHAPE_PRESETS[0]);
};
/** Reposition existing tables into a tidy pattern inside the canvas. */
export const computeArrangement = (kind, tables, canvasWidth, canvasHeight, padding = 48) => {
    const count = tables.length;
    if (count === 0) {
        return {};
    }
    const innerW = Math.max(120, canvasWidth - padding * 2);
    const innerH = Math.max(120, canvasHeight - padding * 2);
    if (kind === 'circle') {
        const cx = canvasWidth / 2;
        const cy = canvasHeight / 2;
        const avgSize = tables.reduce((sum, t) => sum + Math.max(t.width, t.height), 0) / count;
        const radius = Math.max(80, Math.min(innerW, innerH) / 2 - avgSize / 2 - 20);
        const result = {};
        tables.forEach((table, index) => {
            const angle = (Math.PI * 2 * index) / count - Math.PI / 2;
            const x = cx + Math.cos(angle) * radius - table.width / 2;
            const y = cy + Math.sin(angle) * radius - table.height / 2;
            result[table.uuid] = {
                x: clamp(x, 0, canvasWidth - table.width),
                y: clamp(y, 0, canvasHeight - table.height),
                rotation: 0,
            };
        });
        return result;
    }
    if (kind === 'wall') {
        const result = {};
        const edges = [
            'top',
            'right',
            'bottom',
            'left',
        ];
        tables.forEach((table, index) => {
            const edge = edges[index % edges.length];
            const onEdge = Math.floor(index / edges.length);
            const peers = Math.ceil((count - (index % edges.length)) / edges.length);
            const t = peers <= 1 ? 0.5 : (onEdge + 1) / (peers + 1);
            let x = padding;
            let y = padding;
            if (edge === 'top') {
                x = padding + t * (innerW - table.width);
                y = padding;
            }
            else if (edge === 'bottom') {
                x = padding + t * (innerW - table.width);
                y = canvasHeight - padding - table.height;
            }
            else if (edge === 'left') {
                x = padding;
                y = padding + t * (innerH - table.height);
            }
            else {
                x = canvasWidth - padding - table.width;
                y = padding + t * (innerH - table.height);
            }
            result[table.uuid] = {
                x: clamp(x, 0, canvasWidth - table.width),
                y: clamp(y, 0, canvasHeight - table.height),
                rotation: 0,
            };
        });
        return result;
    }
    if (kind === 'rows') {
        const cols = Math.ceil(count / 2);
        const rows = Math.min(2, count);
        return placeGrid(tables, cols, rows, canvasWidth, canvasHeight, padding);
    }
    // grid
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    return placeGrid(tables, cols, rows, canvasWidth, canvasHeight, padding);
};
const placeGrid = (tables, cols, rows, canvasWidth, canvasHeight, padding) => {
    const result = {};
    const cellW = (canvasWidth - padding * 2) / cols;
    const cellH = (canvasHeight - padding * 2) / rows;
    tables.forEach((table, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        const x = padding + col * cellW + (cellW - table.width) / 2;
        const y = padding + row * cellH + (cellH - table.height) / 2;
        result[table.uuid] = {
            x: clamp(x, 0, canvasWidth - table.width),
            y: clamp(y, 0, canvasHeight - table.height),
            rotation: 0,
        };
    });
    return result;
};
export const placementFromTemplateSlot = (slot, canvasWidth, canvasHeight, zIndex, tableCount = 1) => {
    const preset = resolvePresetForSlot(slot);
    const sized = suggestTableSizeForHall(preset.width, preset.height, canvasWidth, canvasHeight, tableCount);
    const x = slot.x * canvasWidth;
    const y = slot.y * canvasHeight;
    const placement = createDefaultTablePlacement(preset.shape, sized.width, sized.height, clamp(x, 0, Math.max(0, canvasWidth - sized.width)), clamp(y, 0, Math.max(0, canvasHeight - sized.height)), zIndex);
    if (typeof slot.rotation === 'number') {
        placement.rotation = slot.rotation;
    }
    return placement;
};
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
